import { apiClient } from './client';

// ============================================================================
// Types
// ============================================================================

export interface Supplier {
  id: string;
  code: string;
  name: string;
  contactName?: string | null;
  /** Some endpoints return both `email` (DB column) and `contactEmail` (legacy alias) — keep both. */
  email?: string | null;
  contactEmail?: string | null;
  phone?: string | null;
  contactPhone?: string | null;
  address?: string | null;
  city?: string | null;
  state?: string | null;
  country?: string | null;
  postalCode?: string | null;
  paymentTerms?: string | null;
  currency?: string;
  defaultLeadTimeDays?: number;
  minimumOrderValue?: number;
  onTimeDeliveryRate?: number | string | null;
  qualityRating?: number | string | null;
  status?: string | null;
  externalId?: string | null;
  /** Free-form JSON populated by Marg sync: margCid, margCompanyId, gstn, route, area, pin, dlNo, etc. */
  attributes?: Record<string, unknown> | null;
  isActive: boolean;
  isPreferred: boolean;
  products?: SupplierProduct[];
}

export interface SupplierProduct {
  id: string;
  supplierId: string;
  productId: string;
  supplier?: { id: string; code: string; name: string };
  product?: { id: string; sku: string; name: string };
  supplyType: 'BUY' | 'MAKE' | 'TRANSFER' | 'SUBCONTRACT';
  supplierPartNumber?: string;
  unitCost?: number;
  currency?: string;
  leadTimeDays?: number;
  minimumOrderQty?: number;
  orderMultiple?: number;
  isPrimary: boolean;
}

// ============================================================================
// Supplier Service
// ============================================================================

export const supplierService = {
  // Suppliers
  async getAll(params?: {
    search?: string;
    isActive?: boolean;
    isPreferred?: boolean;
    page?: number;
    pageSize?: number;
    sortBy?: string;
    sortDir?: 'asc' | 'desc';
    filters?: string;
  }) {
    const response = await apiClient.get('/manufacturing/suppliers', { params });
    return response.data;
  },

  async getById(supplierId: string) {
    const response = await apiClient.get(`/manufacturing/suppliers/${supplierId}`);
    return response.data;
  },

  async create(dto: {
    code: string;
    name: string;
    contactName?: string;
    contactEmail?: string;
    contactPhone?: string;
    address?: string;
    city?: string;
    state?: string;
    country?: string;
    postalCode?: string;
    paymentTerms?: string;
    currency?: string;
    defaultLeadTimeDays?: number;
    minimumOrderValue?: number;
    isActive?: boolean;
    isPreferred?: boolean;
  }) {
    const response = await apiClient.post('/manufacturing/suppliers', dto);
    return response.data;
  },

  async update(supplierId: string, dto: Partial<Supplier>) {
    const response = await apiClient.put(`/manufacturing/suppliers/${supplierId}`, dto);
    return response.data;
  },

  async delete(supplierId: string) {
    await apiClient.delete(`/manufacturing/suppliers/${supplierId}`);
  },

  // Supplier-Product Links
  async getSupplierProducts(
    supplierId: string,
    params?: {
      search?: string;
      supplyType?: string;
      page?: number;
      pageSize?: number;
    },
  ) {
    const response = await apiClient.get(`/manufacturing/suppliers/${supplierId}/products`, { params });
    return response.data;
  },

  // Analytics
  async getPerformance(
    supplierId: string,
    params?: {
      startDate?: string;
      endDate?: string;
    },
  ) {
    const response = await apiClient.get(`/manufacturing/suppliers/${supplierId}/performance`, { params });
    return response.data;
  },
};

export default supplierService;
